import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type CredentialDetail } from '@/api/credentials';
import TagPicker from '@/components/ui/TagPicker';
import TagPill from '@/components/ui/TagPill';
import { tagsApi } from '@/api/tags';
import { useAuthStore } from '@/stores/auth';

// ============================================================
// CredentialTagsSection — afișează + editează tag-urile unui credențial
// ============================================================

export function CredentialTagsSection({ cred }: { cred: CredentialDetail }) {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const queryClient = useQueryClient();

  const tagsList = useQuery({
    queryKey: ['tags', tenantId],
    queryFn: () => tagsApi.list(tenantId!),
    enabled: !!tenantId,
  });

  const attachedSet = new Set(cred.tag_ids);

  const setMutation = useMutation({
    mutationFn: (tagIds: string[]) =>
      tagsApi.setCredentialTags(cred.id, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credential', cred.id] });
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
        predicate: (q) => q.queryKey[0] === 'credentials',
      });
    },
  });

  if (!tenantId) return null;

  const attachedTags = (tagsList.data ?? []).filter((t) =>
    attachedSet.has(t.id),
  );

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="display-title-xs">
          {t('vault_ui.detail.tags_title')}
        </h3>
        <TagPicker
          tenantId={tenantId}
          selected={attachedSet}
          onChange={(next) => setMutation.mutate(Array.from(next))}
        />
      </div>
      {attachedTags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {attachedTags.map((t) => (
            <TagPill
              key={t.id}
              label={t.name}
              color={t.color ?? 'neutral'}
              onRemove={() => {
                const next = new Set(attachedSet);
                next.delete(t.id);
                setMutation.mutate(Array.from(next));
              }}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
          {t('vault_ui.detail.no_tags')}
        </p>
      )}
    </section>
  );
}
